<script lang="ts">
    import cn from 'classnames';
    import { onDestroy, onMount } from 'svelte';
    import { configService } from '$lib/services';
    import { sendPmEvent } from '$lib/services/gtm/methods';

    import { messageUnreadCount } from '@api/room/conversation';
    import { sendPmStats } from '@api/user/stats';
    import { CustomIcon, getIcon } from '@components/icons';
    import { globalStore } from '@components/stores';
    import { isOperator } from '@components/user/store';
    import { LL } from '@i18n/i18n-svelte';
    import { URLS } from '@shared/routing';
    import { EventType, Feature, PmPlace } from '@shared/types';
    import { Font, FontTypes } from '@uikit/font';
    import { IconName, SvgIcon } from '@uikit/svg-icon';
    import { Color } from '@uikit/svg-icon/types';

    import { menuStore } from './store';

    import s from './menu.module.css';

    import { goto } from '$app/navigation';
    import { page } from '$app/stores';
    function onClickHandler(item: { href?: string; onClick?: () => void }) {
        if (item.href) {
            goto(item.href);
        } else if (item.onClick) {
            item.onClick();
        }
    }

    const header = [
        {
            text: $LL.menu.home(),
            icon: IconName.Home,
            href: URLS.roomStart(),
            testId: 'homeButton',
        },
        {
            text: $LL.menu.stories(),
            icon: getIcon(CustomIcon.stories),
            onClick: async () => {
                goto(URLS.storyOpen());
            },
            visible: configService.isFeatureAllowed(Feature.Stories) && !$isOperator,
            testId: 'storiesButton',
        },
        {
            text: $LL.menu.messages(),
            icon: IconName.Message,
            href: URLS.myMessages(),
            onClick: () => {
                sendPmEvent({ event: EventType.PmImpression, place: PmPlace.Menu });
                sendPmStats(
                    EventType.PmImpression,
                    {
                        initiator: $isOperator ? 'model' : 'user',
                        place: PmPlace.Menu,
                    },
                    $globalStore.isDesktopSite
                );
                goto(URLS.myMessages());
            },
            isActive: () => $page.route.id && URLS.isMessage($page.route.id),
            visible: configService.isFeatureAllowed(Feature.Pm),
            testId: 'privateMessagesButton',
        },
        {
            text: $LL.menu.favorites(),
            icon: IconName.Star,
            href: URLS.favorites(),
            testId: 'favoritesButton',
        },
        {
            text: $LL.menu.chatHistory(),
            icon: getIcon(CustomIcon.history),
            href: URLS.chatHistory(),
            testId: 'chatHistoryButton',
        },
    ];

    onMount(() => {
        globalStore.addBodyClass(s.withMenu);
        if ($menuStore.countUnreadMessages === 0) {
            messageUnreadCount().then((countUnreadMessages) => {
                menuStore.set({ countUnreadMessages });
            });
        }
    });

    onDestroy(() => {
        globalStore.deleteBodyClass(s.withMenu);
    });
</script>

<ul class={s.menu} data-testid='menu'>
    {#each header as item}
        {#if item.visible !== false}
            <Font
                    color={FontTypes.Color.Secondary}
                    type={!$globalStore.isDesktopSite ? FontTypes.Type.tiny : FontTypes.Type.body}
                    isBold={!$globalStore.isDesktopSite}
                    tagName={FontTypes.TagName.li}
                    className={cn(s.menuItem, {
                        [s.isActive]: item.isActive && item.isActive() || $page.url.pathname === item.href,
                    })}
                    on:click={() => onClickHandler(item)}
                    attrs={{ 'data-testid': item.testId }}
            >
                <SvgIcon color={
                    item.isActive && item.isActive() || $page.url.pathname === item.href
                        ? Color.white
                        : Color.secondary
                     }
                     icon={item.icon}
                     width={24}
                />
                {#if $menuStore.countUnreadMessages > 0 && item.icon === IconName.Message}
                    <div class={s.redPoint}>{$menuStore.countUnreadMessages}</div>
                {/if}
                <div>{item.text}</div>
            </Font>
        {/if}
    {/each}
</ul>
